import { useState, useEffect, useMemo, useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import {
  CurrentItemIcon,
  EquipmentName,
  EquipmentRowStack,
  NavigationContainer,
  POIName,
  PoiRowStack,
} from '../styles';
import { abyssColors } from '@/theme/colors';
import { usePointOfInterestDetails } from '../../hooks';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { generatePoiDisplayName } from '../../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';

import { PointOfInterestsFragment, useUpdateReportOrderMutation } from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { DraggableList } from '@/components/shared/DraggableList';
import { ReportOrder } from './types';

const scrollToElement = (id: string) => {
  const element = document.querySelector(`#${CSS.escape(id)}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

type ReportNavigatorProps = {
  setOrderUpdateLoader: (loader: boolean) => void;
  orderOfReport: ReportOrder;
  setOrderOfReport: React.Dispatch<
    React.SetStateAction<{
      assemblyOrder: string[];
      poiOrder: {
        [key: string]: string[];
      };
    }>
  >;
};

type EquipmentItem = {
  id: string;
  name: string;
  [key: string]: unknown;
};

export const ReportNavigator = ({
  setOrderUpdateLoader,
  orderOfReport,
  setOrderOfReport,
}: ReportNavigatorProps) => {
  const setSelectedReport = useSetAtom(state.selectedReport);
  const { assemblies, groupPoiByEquipmentWithNumbers: equipmentAndPOIs } =
    usePointOfInterestDetails();
  const router = useRouter();
  const reportId = router?.query?.reportid as string | undefined;
  const [equipmentOrder, setEquipmentOrder] =
    useState<{ id: string; tagName: string }[]>(assemblies);
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined);
  const [updatePoiOrder] = useUpdateReportOrderMutation();

  useEffect(() => {
    if (assemblies) {
      setEquipmentOrder(assemblies);
    }
  }, [assemblies]);

  useEffect(() => {
    if (Object.keys(equipmentAndPOIs).length > 0 && !selectedItem) {
      setSelectedItem(Object.keys(equipmentAndPOIs)[0]);
    }
  }, [equipmentAndPOIs, selectedItem]);

  const navbarHeight = useNavbarHeight();

  const assemblyIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    Object.values(equipmentAndPOIs).forEach((pois) => {
      if (pois.length > 0) {
        map[pois[0].assembly.id] = pois[0].assembly.tagName;
      }
    });
    return map;
  }, [equipmentAndPOIs]);

  useEffect(() => {
    if (orderOfReport?.assemblyOrder) {
      setEquipmentOrder((prev) => {
        const assemblyValue = assemblies.find(
          (assembly) => assembly.id === orderOfReport?.assemblyOrder[0]
        );
        return [...prev, { id: assemblyValue?.id ?? '', tagName: assemblyValue?.tagName ?? '' }];
      });
    }
  }, [orderOfReport?.assemblyOrder, assemblies]);

  const handleClick = (item: string) => {
    setSelectedItem(item);
    scrollToElement(item);
  };

  const getOrderedPOIs = (assemblyId: string): PointOfInterestsFragment[] => {
    const poiIds = orderOfReport?.poiOrder?.[assemblyId] || [];
    const assemblyName = assemblyIdToNameMap[assemblyId];

    let pois: PointOfInterestsFragment[] = [];

    if (assemblyName && equipmentAndPOIs[assemblyName]) {
      pois = equipmentAndPOIs[assemblyName] || [];
    } else {
      Object.values(equipmentAndPOIs).forEach((equipmentPois) => {
        const matchingPois = equipmentPois.filter(
          (poi) => poi.assembly && poi.assembly.id === assemblyId
        );
        if (matchingPois.length > 0) {
          pois = matchingPois;
        }
      });
    }

    if (poiIds.length > 0) {
      const poiMap = new Map(pois.map((poi) => [poi.id, poi]));

      const result = poiIds
        .map((id) => {
          const found = poiMap.get(id);
          return found;
        })
        .filter(Boolean) as PointOfInterestsFragment[];

      return result;
    } else {
      return pois as PointOfInterestsFragment[];
    }
  };

  const equipmentItems: EquipmentItem[] = useMemo(() => {
    if (orderOfReport?.assemblyOrder.length > 0) {
      return orderOfReport.assemblyOrder.map((k: string) => ({
        id: k,
        name: assemblyIdToNameMap[k],
      }));
    }
    return equipmentOrder.map(({ id, tagName }) => ({ id, name: tagName }));
  }, [equipmentOrder, orderOfReport?.assemblyOrder, assemblyIdToNameMap]);

  const buildOrderPayload = (poiOrder: { [key: string]: string[] }): Record<string, string[]> =>
    Object.fromEntries(Object.entries(poiOrder).map(([equip, poiIds]) => [equip, poiIds]));

  const submitReportOrder = useCallback(
    (poiOrder: { [key: string]: string[] }, assemblyOrder: string[]) => {
      if (!reportId) return;
      setOrderUpdateLoader(true);
      const poiOrderObject = buildOrderPayload(poiOrder);
      try {
        updatePoiOrder({
          variables: {
            input: {
              reportId,
              reportOrder: {
                assemblyOrder: assemblyOrder,
                poiOrder: poiOrderObject,
              },
            },
          },
        })
          .then((response) => {
            setSelectedReport((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                reportOrder: {
                  assemblyOrder: response.data?.updateReport?.reportOrder?.assemblyOrder,
                  poiOrder: response.data?.updateReport?.reportOrder?.poiOrder,
                },
              };
            });
          })
          .finally(() => {
            setOrderUpdateLoader(false);
          });
      } catch (e) {
        console.error('Failed to save POI order', e);
      }
    },
    [reportId, updatePoiOrder, setOrderUpdateLoader, setSelectedReport]
  );

  const handleEquipmentOrderChange = useCallback(
    (newOrder: EquipmentItem[]) => {
      const equipmentOrder = newOrder.map((e) => e.id);

      const updatedOrder = {
        ...orderOfReport,
        assemblyOrder: equipmentOrder,
      };
      setEquipmentOrder(
        newOrder.map(({ id, tagName }) => ({ id, tagName: String(tagName || '') }))
      );
      setOrderOfReport(updatedOrder);

      submitReportOrder(orderOfReport?.poiOrder || {}, equipmentOrder);
    },
    [orderOfReport, setEquipmentOrder, setOrderOfReport, submitReportOrder]
  );
  const handlePoiOrderChange = useCallback(
    (assemblyId: string) => (newOrder: PointOfInterestsFragment[]) => {
      if (!assemblyId) return;

      const updated = {
        ...orderOfReport,
        poiOrder: {
          ...orderOfReport?.poiOrder,
          [assemblyId]: newOrder.map((poi) => poi.id),
        },
      };
      setOrderOfReport(updated);
      submitReportOrder(updated.poiOrder, updated.assemblyOrder);
    },
    [orderOfReport, setOrderOfReport, submitReportOrder]
  );

  return (
    <NavigationContainer navbarHeight={navbarHeight} data-testId="report-navigation">
      <Typography
        color={abyssColors.primary[700]}
        fontWeight={700}
        m={3}
        data-testid="report-navigator-title"
      >
        Contents
      </Typography>
      <Stack gap={3} marginBottom={6}>
        <DraggableList
          items={equipmentItems}
          onChange={handleEquipmentOrderChange}
          renderItem={(equip) => {
            const key = equip.id;
            const orderedPOIs = getOrderedPOIs(key);
            return (
              <DraggableList.Item id={key}>
                <Stack key={key} data-testid="report-navigator-equipment">
                  <EquipmentRowStack direction="row" alignItems="center" marginBottom="12px">
                    <CurrentItemIcon selected={selectedItem === key} />
                    <EquipmentName
                      selected={selectedItem === key}
                      onClick={() => {
                        handleClick(key);
                      }}
                      sx={{ flex: 1 }}
                    >
                      {equip?.name || ''}
                    </EquipmentName>
                    <DraggableList.DragHandle className="drag-handle" />
                  </EquipmentRowStack>
                  <DraggableList
                    items={orderedPOIs.length > 0 ? orderedPOIs : equipmentAndPOIs[key] || []}
                    onChange={handlePoiOrderChange(key)}
                    renderItem={(point) => (
                      <DraggableList.Item id={point.id}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          marginBottom="4px"
                          data-testid="report-navigator-poi"
                        >
                          <CurrentItemIcon selected={selectedItem === point.id} />
                          <Stack direction="row" justifyContent="space-between" width={`100%`}>
                            <PoiRowStack direction="row" justifyContent={'space-between'}>
                              <POIName
                                selected={selectedItem === point.id}
                                onClick={() => {
                                  handleClick(point.id);
                                }}
                                sx={{
                                  maxWidth: '90%',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {generatePoiDisplayName({
                                  name: point.name,
                                  poiType: point.type,
                                  poiId: point.poiId,
                                })}
                              </POIName>
                              <DraggableList.DragHandle className="drag-handle" />
                            </PoiRowStack>
                          </Stack>
                        </Stack>
                      </DraggableList.Item>
                    )}
                  />
                </Stack>
              </DraggableList.Item>
            );
          }}
        />
      </Stack>
    </NavigationContainer>
  );
};
